import { Link } from "react-router-dom";
import { SiteHeader } from "../components/SiteHeader";
import { SiteFooter } from "../components/SiteFooter";
import { useBookCall } from "../components/BookCallDialog";
import { useSeo, breadcrumbSchema } from "../lib/seo";
import { Button } from "../components/ui/button";
import { ARTICLES } from "../data/articles";
import { ArrowUpRight, Clock, Calendar } from "lucide-react";

const COPY = {
  en: {
    title: "Blog & Insights — Munich Furniture Assembly Guide for Expats | MUConcierge",
    description:
      "MUConcierge blog: articles, guides and resources on furniture assembly, IKEA setup, apartment move-in concierge, and relocation life in Munich for English-speaking expats.",
    tag: "Blog & Insights",
    h1: "Furniture assembly Munich — insights, guides and articles for expats.",
    intro:
      "Practical guides, deep dives and field notes on furniture assembly, apartment setup, IKEA logistics and the Munich expat experience — written by the people who handle the keys.",
    ctaBook: "Book a free discovery call",
    readMore: "Read article",
    minRead: "min read",
    backHome: "← Back to MUConcierge home",
    finalCtaTitle: "Munich apartment, move-in ready?",
    finalCtaSub: "Book a free 20-minute discovery call — we reply within 4 business hours.",
  },
  de: {
    title: "Blog & Insights — Münchner Möbelmontage-Guide für Expats | MUConcierge",
    description:
      "MUConcierge-Blog: Artikel, Guides und Ressourcen zu Möbelmontage, IKEA-Aufbau, Wohnungseinrichtung und Münchner Expat-Leben für englischsprachige internationale Fachkräfte.",
    tag: "Blog & Insights",
    h1: "Möbelmontage München — Wissen, Guides und Artikel für Expats.",
    intro:
      "Praktische Guides, tiefe Einblicke und Erfahrungsberichte zu Möbelmontage, Wohnungseinrichtung, IKEA-Logistik und dem Münchner Expat-Alltag — geschrieben von den Menschen, die die Schlüssel übernehmen.",
    ctaBook: "Kostenloses Beratungsgespräch buchen",
    readMore: "Artikel lesen",
    minRead: "Min. Lesezeit",
    backHome: "← Zurück zur MUConcierge-Startseite",
    finalCtaTitle: "Wohnung in München einzugsfertig?",
    finalCtaSub: "Buchen Sie ein kostenloses 20-Minuten-Beratungsgespräch — Antwort innerhalb von 4 Werktagsstunden.",
  },
};

export default function MorePage({ lang = "en" }) {
  const t = COPY[lang];
  const { openDialog } = useBookCall();
  const homePath = lang === "de" ? "/de" : "/";
  const basePath = lang === "de" ? "/de/mehr" : "/more";
  const origin = typeof window !== "undefined" ? window.location.origin : "https://muconcierge.com";

  useSeo({
    title: t.title,
    description: t.description,
    path: basePath,
    lang,
    alternates: { en: "/more", de: "/de/mehr" },
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "Blog",
        name: t.title,
        description: t.description,
        url: origin + basePath,
        blogPost: ARTICLES.map((a) => ({
          "@type": "BlogPosting",
          headline: a[lang].title,
          datePublished: a.publishedAt,
          url: origin + `${basePath}/${a[lang].slug}`,
          description: a[lang].excerpt,
          author: { "@type": "Organization", name: "MUConcierge" },
        })),
      },
      breadcrumbSchema([
        { name: lang === "de" ? "Startseite" : "Home", url: origin + homePath },
        { name: lang === "de" ? "Mehr" : "More", url: origin + basePath },
      ]),
    ],
  });

  const dateLabel = (iso) =>
    new Date(iso).toLocaleDateString(lang === "de" ? "de-DE" : "en-GB", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <SiteHeader />

      {/* HERO */}
      <section className="max-w-5xl mx-auto px-6 sm:px-8 md:px-12 pt-20 md:pt-24 pb-10" data-testid="more-hero">
        <p className="tag-line text-[#C26D5C] mb-4">{t.tag}</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#1A201C] tracking-tight leading-[1.05] max-w-3xl">
          {t.h1}
        </h1>
        <p className="mt-6 text-[#5C635A] leading-relaxed font-light text-lg max-w-2xl">{t.intro}</p>
      </section>

      {/* ARTICLES */}
      <section className="max-w-5xl mx-auto px-6 sm:px-8 md:px-12 pb-20" data-testid="more-articles">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {ARTICLES.map((a, idx) => {
            const local = a[lang];
            const href = `${basePath}/${local.slug}`;
            return (
              <article
                key={a.id}
                className={`group bg-white border border-[#E5E2DC] hover:border-[#1C3326] transition-colors duration-300 flex flex-col ${idx === 0 ? "md:col-span-2" : ""}`}
                data-testid={`article-card-${a.id}`}
              >
                <Link to={href} className="block overflow-hidden" data-testid={`article-image-${a.id}`}>
                  <img
                    src={a.coverImage}
                    alt={a.coverAlt[lang]}
                    className={`w-full object-cover transition-transform duration-700 group-hover:scale-105 ${idx === 0 ? "h-72 md:h-96" : "h-56"}`}
                    loading="lazy"
                  />
                </Link>
                <div className="p-6 md:p-8 flex flex-col flex-grow">
                  <p className="tag-line text-[#C26D5C] mb-3">{local.category}</p>
                  <h2 className={`font-serif text-[#1A201C] tracking-tight leading-[1.15] ${idx === 0 ? "text-2xl md:text-3xl" : "text-xl md:text-2xl"}`}>
                    <Link to={href} className="hover:text-[#1C3326]" data-testid={`article-title-${a.id}`}>
                      {local.title}
                    </Link>
                  </h2>
                  <p className="mt-3 text-[#5C635A] leading-relaxed font-light text-sm flex-grow">{local.excerpt}</p>
                  <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-[#5C635A]">
                    <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" strokeWidth={1.5} /> {dateLabel(a.publishedAt)}</span>
                    <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" strokeWidth={1.5} /> {a.readMinutes} {t.minRead}</span>
                  </div>
                  <div className="mt-6">
                    <Link
                      to={href}
                      className="inline-flex items-center gap-1 text-sm text-[#1C3326] underline underline-offset-4 decoration-[#C26D5C]/40 hover:decoration-[#C26D5C]"
                      data-testid={`article-link-${a.id}`}
                    >
                      {t.readMore} <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </Link>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-[#1C3326]">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 md:px-12 py-14 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl text-[#FAF9F6] tracking-tight leading-[1.1]">{t.finalCtaTitle}</h2>
          <p className="mt-4 text-[#FAF9F6]/80 max-w-xl mx-auto font-light">{t.finalCtaSub}</p>
          <Button
            size="lg"
            onClick={() => openDialog({ source: `more-${lang}-final`, service: lang === "de" ? "Beratungsgespräch" : "Discovery call" })}
            className="mt-8 rounded-sm bg-[#FAF9F6] text-[#1C3326] hover:bg-[#E8C9B6] px-10 py-6 text-base"
            data-testid="more-cta-final"
          >
            {t.ctaBook}
            <ArrowUpRight className="ml-2 w-4 h-4" strokeWidth={1.5} />
          </Button>
          <div className="mt-6">
            <Link to={homePath} className="text-xs text-[#FAF9F6]/60 underline underline-offset-4 hover:text-[#E8C9B6]" data-testid="more-back-home">
              {t.backHome}
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
